import "@hotwired/turbo-rails";
import gsap from "gsap";

import "./controllers";
import { addClickListener } from "./utils/events";
import { remToPx } from "./utils/sizing";

import "../../libs/turbo";

const setCookieYesToPermanent = () => {
  const identifiersObj = {
    "cky-overlay": false,
    "cky-btn-revisit-wrapper": false,
    "cky-consent-container": false,
    "cky-modal": false,
  };
  const identifiers = Object.keys(identifiersObj);

  const observer = new MutationObserver((mutations, observer) => {
    for (const mutation of mutations) {
      for (const addedNode of mutation.addedNodes) {
        for (const identifier of identifiers) {
          if (!addedNode.classList) {
            continue;
          }

          if (addedNode.classList.contains(identifier)) {
            identifiersObj[identifier] = true;
            addedNode.dataset.turboPermanent = true;
            addedNode.id = `cookieyes-uid-${identifier}`;
          }
        }
      }
    }

    if (Object.values(identifiersObj).every((x) => x)) {
      observer.disconnect();
    }
  });

  observer.observe(document.body, { childList: true });
};

setCookieYesToPermanent();

class Application {
  constructor() {
    this.$loader = document.body.querySelector(".loader");
  }

  afterRender() {
    document.body.classList.remove("page--loading");
    document.body.classList.remove("scroll-none");
    document.body.classList.remove("--popup-opened");
    document.body.classList.add("page--ready");
    const clickedFlavour = document.querySelector(".flavour-link.clicked");
    if (clickedFlavour) {
      clickedFlavour.classList.remove("clicked");
      clickedFlavour.classList.add("group/pastille");
    }
    this.playVideos();
  }

  beforeRender(event) {
    event.preventDefault();

    const loadTime = (event.timeStamp - this.leaveTime) / 1000;
    const delayDuration = loadTime < 0.5 ? 0.5 - loadTime : 0;
    document.body.classList.remove("--burger-opened");

    event.detail.newBody.classList.remove("page--leaving");

    this.detectSafari(event.detail.newBody);

    gsap.delayedCall(
      delayDuration,
      function () {
        event.detail.resume();
      }.bind(this),
    );
  }

  beforeVisit(event) {
    if (this.clickedFlavour != null) {
      document.body.classList.add("page--flavour");
      this.clickedFlavour.classList.add("clicked");
      this.clickedFlavour.classList.remove("group/pastille");
    }
    document.body.classList.remove("--burger-opened");
    document.body.classList.remove("--header-scrolled");
    document.body.classList.add("page--loading");
    document.body.classList.add("page--leaving");
    document.body.classList.remove("page--ready");
    document.body.classList.remove("disable-animations");

    this.leaveTime = event.timeStamp;
  }

  detectSafari(body) {
    const UA = window.navigator.userAgent;
    const isWebkit =
      /\b(iPad|iPhone|iPod|Macintosh)\b/.test(UA) &&
      /WebKit/.test(UA) &&
      !/Edge/.test(UA) &&
      !/Chrome/.test(UA) &&
      !window.MSStream;

    if (isWebkit) {
      body.classList.add("safari");
    }
  }

  init() {
    this.detectSafari(document.body);

    addClickListener(document.documentElement, 'a[href*="#"]', this.handleSmoothScrollTo.bind(this));
    addClickListener(document.documentElement, "a.flavour-link", this.onClickFlavour.bind(this));

    this.$loader.classList.add("active");
    document.body.classList.remove("page--loading");
    document.addEventListener("turbo:load", this.onLoad.bind(this));
    document.addEventListener("turbo:before-render", this.beforeRender.bind(this));
    document.addEventListener("turbo:before-visit", this.beforeVisit.bind(this));
    document.addEventListener("turbo:render", this.afterRender.bind(this));
  }

  onClickFlavour(event) {
    this.clickedFlavour = event.target.closest("a");
  }

  onLoad(event) {
    if (this.$loader.classList.contains("active")) {
      const loadTime = event.timeStamp / 1000;
      this.removeLoader(loadTime);
    }
  }

  handleSmoothScrollTo(event) {
    const link = event.target.closest("a");
    if (!link) return;

    const hash = link.getAttribute("href").split("#")[1];
    const pageUrl = link.getAttribute("href").split("#")[0];

    if (pageUrl != "" && pageUrl != window.location.origin + window.location.pathname) return;

    const target = document.querySelector("#" + hash);
    if (!(target instanceof Element)) return;

    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - remToPx(15),
      behavior: "smooth",
    });

    event.preventDefault();
  }

  playVideos() {
    document.querySelectorAll("video[autoplay]").forEach((video) => {
      video.play();
    });
  }

  async removeLoader(loadTime) {
    if (import.meta.env.DEV) {
      this.$loader.classList.remove("active");
      document.querySelector(".loader").remove();
      document.body.classList.remove("scroll-none");
      document.body.classList.add("page--ready");
      return;
    }

    const delayDuration = loadTime < 1.5 ? 1.5 - loadTime : 0;
    let tl = gsap.timeline();

    tl.to(this.$loader.querySelector(".red"), {
      duration: delayDuration + 0.9,
      ease: "power1.out",
      transform: "scale(1.03)",
    })
      .to(
        this.$loader.querySelector(".beige"),
        { duration: delayDuration + 0.9, ease: "power1.out", transform: "scale(1.03)" },
        "<",
      )
      .to(
        this.$loader.querySelector(".beige-transparent"),
        { duration: delayDuration + 0.9, ease: "power1.out", transform: "scale(1.03)" },
        "<",
      )
      .to(
        this.$loader.querySelector(".white-dots"),
        { duration: delayDuration + 0.9, ease: "power1.out", transform: "scale(1.035)" },
        "<",
      )
      .to(
        this.$loader.querySelector(".color-dots"),
        { duration: delayDuration + 0.9, ease: "power1.out", transform: "scale(1.035)" },
        "<",
      )
      .to(
        this.$loader.querySelector(".leaves"),
        { duration: delayDuration + 0.9, ease: "power1.out", transform: "scale(1.04)" },
        "<",
      )
      .to(
        this.$loader.querySelector(".beans"),
        { duration: delayDuration + 0.9, ease: "power1.out", transform: "scale(1.04)" },
        "<",
      )
      .to(
        this.$loader.querySelector(".brown-waves"),
        { duration: delayDuration + 0.9, ease: "power1.out", transform: "scale(1.042)" },
        "<",
      )
      .to(
        this.$loader.querySelector(".branch"),
        { duration: delayDuration + 0.9, ease: "power1.out", transform: "scale(1.04)" },
        "<",
      )
      .to(this.$loader, { delay: delayDuration, duration: 0.8, opacity: 0 }, "<")
      .then(() => {
        this.$loader.classList.remove("active");
        document.querySelector(".loader").remove();
      });
    gsap.delayedCall(
      delayDuration + 0.1,
      function () {
        document.body.classList.remove("scroll-none");
        document.body.classList.add("page--ready");
        if (window.location.hash) {
          const target = document.querySelector(window.location.hash);
          if (target instanceof Element) {
            window.scrollTo({
              top: target.getBoundingClientRect().top + window.scrollY - remToPx(15),
              behavior: "smooth",
            });
          }
        }
      }.bind(this),
    );
  }
}

const app = new Application();
app.init();

window.addEventListener("pageshow", function (event) {
  var historyTraversal =
    event.persisted || (typeof window.performance != "undefined" && window.performance.navigation.type === 2);

  if (historyTraversal && document.body.dataset.reloadOnBack) {
    // Handle page restore.
    window.location.reload();
  }
});
